import type { BadgeEarned } from "../../hooks/useProgressReport";
import type { DisciplineSlug } from "@jumpbean/shared";

export interface BadgesSectionProps {
  badges: BadgeEarned[];
}

// Discipline colors for badge backgrounds
const disciplineColors: Record<DisciplineSlug, string> = {
  math: "from-magic-100 to-luna-light/50 border-magic-200",
  spelling: "from-meadow-100 to-petal-light/50 border-meadow-200",
  patterns: "from-fairy-100 to-sunshine-100 border-fairy-200",
  typing: "from-sky-100 to-whiskers-light/50 border-sky-200",
  programming: "from-pixel-light/50 to-sunshine-100 border-pixel-light",
};

/**
 * Badges section showing achievements earned during the period.
 * Features celebratory styling with discipline-themed colors.
 */
export function BadgesSection({ badges }: BadgesSectionProps) {
  if (badges.length === 0) {
    return (
      <div className="space-y-4">
        <h2 className="font-display font-bold text-display-sm text-neutral-800 flex items-center gap-2">
          <span className="text-xl">🏅</span>
          Badges Earned
        </h2>

        <div className="bg-neutral-50 rounded-xl p-6 text-center border border-neutral-100 border-dashed">
          <span className="text-3xl mb-2 block opacity-60">🏅</span>
          <p className="font-body text-neutral-500">
            No new badges this period
          </p>
          <p className="font-body text-sm text-neutral-400 mt-1">
            Complete all challenges in a level to earn a badge!
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h2 className="font-display font-bold text-display-sm text-neutral-800 flex items-center gap-2">
        <span className="text-xl">🏅</span>
        Badges Earned
        <span className="ml-2 px-2 py-0.5 bg-sunshine-100 text-sunshine-700 rounded-full font-body text-sm font-medium">
          {badges.length} new
        </span>
      </h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {badges.map((badge) => {
          const colorClass =
            disciplineColors[badge.disciplineSlug as DisciplineSlug] ||
            "from-neutral-100 to-neutral-50 border-neutral-200";

          return (
            <div
              key={badge.id}
              className={`relative overflow-hidden rounded-xl bg-gradient-to-br ${colorClass} border p-4 transition-transform hover:scale-[1.02] print:hover:scale-100`}
            >
              {/* Decorative sparkle */}
              <div className="absolute top-2 right-2 text-lg opacity-50 print:hidden">
                ✨
              </div>

              <div className="flex items-center gap-3">
                {/* Badge icon */}
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-sunshine-400 to-sunshine-500 flex items-center justify-center shadow-md flex-shrink-0">
                  {badge.imageUrl ? (
                    <img
                      src={badge.imageUrl}
                      alt=""
                      className="w-8 h-8 object-contain"
                    />
                  ) : (
                    <span className="text-2xl">🏅</span>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <p className="font-display font-semibold text-body-md text-neutral-800 truncate">
                    {badge.name}
                  </p>
                  <p className="font-body text-sm text-neutral-600">
                    {badge.disciplineName} Level {badge.levelNumber}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default BadgesSection;
